import React from "react";
import { Link } from "react-router-dom";
import { pageLink } from "@/lib/landerDomain";
import { ArrowSquareOut, Eye, PaperPlaneTilt, PencilSimple } from "@phosphor-icons/react";

const KIND = {
  shared: ["Shared page", "bg-black text-white"],
  own: ["Their own page", "bg-amber-300 text-black"],
  direct: ["Old page link", "bg-zinc-200 text-black"],
};
const LINK = "inline-flex items-center gap-1 border border-black px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "No leads yet");

function Stat({ label, value, testId }) {
  return (
    <div className="min-w-[56px]" data-testid={testId}>
      <div className="font-chivo text-lg font-black leading-none">{value}</div>
      <div className="mt-0.5 text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}

const pagePreview = (path) => `${path}${path.includes("?") ? "&" : "?"}preview=1`;

// One page that can send this buyer leads: counts, links, and the thank-you page it shows.
export function LeadSourceRow({ row, p, onThankYou, onTest, testing }) {
  const [kindLabel, kindStyle] = KIND[row.kind];
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-zinc-200 px-3 py-3 last:border-b-0 ${row.picked ? "" : "bg-zinc-50"}`} data-testid={p}>
      <div className="min-w-[220px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${kindStyle}`} data-testid={`${p}-kind`}>{kindLabel}</span>
          <b className="text-sm" data-testid={`${p}-title`}>{row.title}</b>
          {row.status && row.status !== "published" && <span className="border border-amber-700 px-1 text-[9px] uppercase tracking-widest text-amber-800">{row.status}</span>}
          {!row.picked && <span className="border border-zinc-500 px-1 text-[9px] uppercase tracking-widest text-zinc-600" data-testid={`${p}-not-picked`}>Not picked</span>}
        </div>
        <div className="mt-1 break-all font-mono text-[10px] text-zinc-600">{row.path}</div>
        <div className="mt-1 text-[10.5px] text-zinc-600" data-testid={`${p}-ty-design`}>Thank-you page: <b>{row.thank_you_design}</b></div>
        {!row.picked && (
          <div className="text-[10.5px] text-zinc-500">
            Not one of their shared pages{row.matched > 0 ? `; ${row.matched} of these leads were matched to them from visitors' answers` : ", but it still sent them leads"}.
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <Stat label="Leads" value={row.leads} testId={`${p}-leads`} />
        <Stat label="Delivered" value={row.delivered} testId={`${p}-delivered`} />
        {row.kind === "shared" && <Stat label="Visits" value={row.visits} testId={`${p}-visits`} />}
        <div className="min-w-[110px] self-center text-[10.5px] text-zinc-600" data-testid={`${p}-last`}>
          {when(row.last_lead_at)}
          {row.test_leads > 0 && <div className="text-amber-800">{row.test_leads} test</div>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <a href={pageLink(pagePreview(row.path), !row.status || row.status === "published")} target="_blank" rel="noopener noreferrer" className={LINK} data-testid={`${p}-open`}>
          <ArrowSquareOut size={11} weight="bold" /> Page
        </a>
        <button type="button" onClick={() => onThankYou(row)} className={LINK} data-testid={`${p}-ty`}>
          <Eye size={11} weight="bold" /> Thank-you page
        </button>
        {row.page_id && (
          <Link to={`/admin/landing-pages/custom/${row.page_id}`} className={LINK} data-testid={`${p}-edit`}>
            <PencilSimple size={11} weight="bold" /> Edit
          </Link>
        )}
        {onTest && row.kind !== "direct" && (
          <button type="button" onClick={() => onTest(row)} disabled={testing} className={`${LINK} bg-amber-300 disabled:opacity-50`} data-testid={`${p}-test`}>
            <PaperPlaneTilt size={11} weight="bold" /> {testing ? "Sending…" : "Test + thank-you"}
          </button>
        )}
      </div>
    </div>
  );
}
